import * as React from 'react';

import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Col, Modal, Row, Spin, notification } from 'antd';
import { Document, Page } from 'react-pdf';
import { getFacilityHealthAndSafetyDocument, setHasAcceptedFacilityHealthAndSafety } from '@/store/modules';
import { useAppDispatch, useAppSelector } from '@/types/reduxHelpers';

import { AnimatedHeader } from '@/components/animatedHeader';
import { AppButton } from '@/components/appButton';
import { PageWrapper } from '@/components/pageWrapper';
import { WelcomeHeader } from '@/components/pageHeader';
import { getPdfBlobFromRawData } from '@/utility/pdf';
import { useApiErrorMessage } from '@/hooks/useApiErrorMessage';
import { useAppRoutes } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FacilityHealthAndSafetyPageProps {

}

export const FacilityHealthAndSafetyPage: React.FC<FacilityHealthAndSafetyPageProps> = (props) => {
    const { t } = useTranslation();
    const { getErrorMessage } = useApiErrorMessage();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const appRoutes = useAppRoutes();
    const deviceConfig = useAppSelector((state) => state.deviceConfig.deviceConfig);
    const visitor = useAppSelector((state) => state.signIn.visitor);
    const visitDetails = useAppSelector((state) => state.signIn.visitDetails);

    const [pdfBlob, setPdfBlob] = React.useState<Blob | undefined>(undefined);
    const [numberOfPages, setNumberOfPages] = React.useState(0);
    const [pages, setPages] = React.useState<Array<number>>([]);
    const [isPdfLoaded, setIsPdfLoaded] = React.useState(false);

    React.useEffect(() => {
        const loadFacilityHealthAndSafetyDocumentAsync = async (): Promise<void> => {
            if (!!deviceConfig?.facilityId) {

                const loadAction = await dispatch(getFacilityHealthAndSafetyDocument({
                    facilityId: deviceConfig.facilityId,
                }));

                if (getFacilityHealthAndSafetyDocument.rejected.match(loadAction)) {
                    notification.error({
                        message: t('Common.error'),
                        description: getErrorMessage(loadAction.payload),
                    });
                } else {
                    if (!loadAction.payload) {
                        // The request succeeded, but no health and safety document was found for this facility
                        // Say it was accepted to pass any validations down the line...
                        dispatch(setHasAcceptedFacilityHealthAndSafety(true));
                        // ...and move on to the evacuation map page
                        navigate(appRoutes.facilityEvacuationMap.path, { replace: true });

                        return;
                    }

                    setPdfBlob(getPdfBlobFromRawData(loadAction.payload.healthAndSafetyDocumentData));
                }
            }

        };

        if (!visitor || !visitDetails) {
            navigate(appRoutes.home.path, { replace: true });

            return;
        }

        loadFacilityHealthAndSafetyDocumentAsync();
    }, []);

    React.useEffect(() => {
        const newPages: Array<number> = [];

        for (let i = 0; i < numberOfPages; i++) {
            newPages.push(i + 1);
        }

        setPages(newPages);
    }, [numberOfPages]);

    return (
        <PageWrapper
            pageTitle={<WelcomeHeader />}
            containerClassName="agreement-page"
            isLoading={!isPdfLoaded}
            scrollBehaviorOnMount="none"
        >
            <Row
                align="middle"
                justify="center"
                gutter={20}
            >
                <Col
                    span={24}
                >
                    <AnimatedHeader>
                        {t('Pages.FacilityHealthAndSafetyPage.header')}
                    </AnimatedHeader>
                </Col>

                <Col
                    style={{ textAlign: 'center' }}
                    span={24}
                >
                    <Document
                        file={pdfBlob}
                        onLoadSuccess={(doc) => {
                            setNumberOfPages(doc.numPages);
                            setIsPdfLoaded(true);
                        }}
                        onLoadError={(e) => console.log(e)}
                        className="pdf-document"
                        renderMode="canvas"
                    >
                        {pages.map((page) => {
                            return (
                                <Page
                                    key={page}
                                    className="pdf-page"
                                    pageNumber={page}
                                    renderTextLayer={false}
                                    loading={<Spin style={{ height: '100%' }} />}
                                    scale={2}
                                />
                            );
                        })}
                    </Document>
                </Col>

                <Col
                    span={24}
                    style={{ marginTop: 15 }}
                >
                    <Row
                        align="middle"
                        justify="space-between"
                        gutter={20}
                    >
                        <Col
                            xxl={10}
                            xl={10}
                            lg={12}
                            md={12}
                            sm={12}
                            xs={24}
                        >
                            <AppButton
                                style={{ width: '100%', minHeight: '120px', margin: '10px 0' }}
                                icon={<CloseCircleOutlined />}
                                onClick={() => {
                                    Modal.confirm({
                                        title: t('Common.confirmation'),
                                        content: (
                                            <div>
                                                <p>{t('Pages.FacilityHealthAndSafetyPage.decline_agreement_warning')}</p>
                                                <p>{t('Pages.FacilityHealthAndSafetyPage.decline_agreement_confirmation')}</p>
                                            </div>
                                        ),
                                        onOk: () => {
                                            navigate(appRoutes.home.path);
                                        },
                                        okText: t('Pages.FacilityHealthAndSafetyPage.decline_agreement_confirmation_button_text'),
                                        cancelText: t('Common.go_back'),
                                    });
                                }}
                                disabled={!isPdfLoaded}
                            >
                                {t('Common.decline')}
                            </AppButton>
                        </Col>

                        <Col
                            xxl={10}
                            xl={10}
                            lg={12}
                            md={12}
                            sm={12}
                            xs={24}
                        >
                            <AppButton
                                style={{ width: '100%', minHeight: '120px', margin: '10px 0' }}
                                icon={<CheckCircleOutlined />}
                                onClick={() => {
                                    dispatch(setHasAcceptedFacilityHealthAndSafety(true));
                                    navigate(appRoutes.facilityEvacuationMap.path);
                                }}
                                disabled={!isPdfLoaded}
                            >
                                {t('Common.accept')}
                            </AppButton>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </PageWrapper>
    );
};